import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import Moment from 'moment';
import Styled from 'styled-components/macro';
import Util from '../shared/util/util';
import UpdateClientMutation from '../../mutations/update_client_mutation';
import {RichTextEditorDraftJsWrapper} from '../shared/components/rich-text-editor-draftjs-wrapper/RichTextEditorDraftJsWrapper';
import ForecastTooltip from '../shared/components/tooltips/ForecastTooltip';
import {
	isBlankDraftJsContent,
	isSameDraftJsContent,
	trimDraftJsEdges,
} from '../shared/components/rich-text-editor-draftjs-wrapper/draftJsContentEquals';

// Design tokens (Figma: Company Viewscreen — sky design system)
// sky/sys/color/text/content/main
const COLOR_TEXT_MAIN = '#232334';
// sky/sys/color/text/content/primary (links + low-emphasis buttons)
const COLOR_TEXT_PRIMARY = '#0004db';
// sky/sys/color/text/content/secondary (low-emphasis meta text, e.g. "Updated: …")
const COLOR_TEXT_SECONDARY = '#6b6d7b';
// sky/sys/color/border/decorative
const COLOR_BORDER = '#d3d3df';
// sky/sys/color/background/neutral/1
const COLOR_SURFACE = '#ffffff';
// sky/sys/color/background/neutral/2
const COLOR_SURFACE_RAISED = '#fcfbfd';
// sky/sys/color/text/content/disabled
const COLOR_TEXT_DISABLED = '#a1a1ad';
// sky/sys/color/background/disabled/1
const COLOR_SURFACE_DISABLED = '#f5f3f9';

// IGNITE-479 is a shell: the Guest Users (Main) and Notes panels are rendered in their EMPTY-STATE
// variation only. Populated data + the Contacts-module variant are wired in later tickets.
const Layout = Styled.div`
	display: flex;
	flex: 1 1 auto;
	align-items: stretch;
	/* 32px gap between the header/info bar and the content (Figma: header ends y=152, content y=184). */
	margin-top: 32px;
	border-top: 1px solid ${COLOR_BORDER};
`;

const MainColumn = Styled.div`
	flex: 1 1 auto;
	min-width: 0;
	padding: 16px 32px 32px 32px;
`;

const SectionHeader = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 44px;
	margin-bottom: 16px;
`;

const SectionTitle = Styled.h2`
	${props => props.theme.sys.font.heading['4']};
	color: ${COLOR_TEXT_MAIN};
	margin: 0;
`;

const CreateButton = Styled.button`
	display: inline-flex;
	align-items: center;
	height: 44px;
	padding: 0 16px;
	border: 1px solid ${COLOR_BORDER};
	border-radius: 8px;
	background-color: ${COLOR_SURFACE};
	${props => props.theme.sys.font.label['1']};
	color: ${COLOR_TEXT_MAIN};
	cursor: pointer;
`;

// Shared empty-state card used by both the Guest Users and Notes panels.
const EmptyCard = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	width: 100%;
	min-height: 240px;
	padding: 24px;
	border: 1px solid ${COLOR_BORDER};
	border-radius: 8px;
	background-color: ${COLOR_SURFACE};
`;

const EmptyContent = Styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	max-width: 331px;
	text-align: center;
`;

const EmptyMessage = Styled.p`
	margin: 0;
	${props => props.theme.sys.font.body['1']};
	color: ${COLOR_TEXT_MAIN};
`;

// Low-emphasis button (sky/comp/button low emphasis): blue text, no chrome.
const EmptyActionButton = Styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	height: 44px;
	padding: 0 12px;
	border: none;
	background: none;
	${props => props.theme.sys.font.label['1']};
	color: ${COLOR_TEXT_PRIMARY};
	cursor: pointer;

	&:disabled {
		color: ${COLOR_TEXT_DISABLED};
		cursor: default;
	}
`;

const NotesPanel = Styled.aside`
	display: flex;
	flex-direction: column;
	flex: 0 0 512px;
	width: 512px;
	border-left: 1px solid ${COLOR_BORDER};
`;

const NotesHeader = Styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 16px;
	height: 60px;
	padding: 0 16px 0 24px;
	background-color: ${COLOR_SURFACE_RAISED};
	border-bottom: 1px solid ${COLOR_BORDER};
`;

const NotesTitle = Styled.h2`
	${props => props.theme.sys.font.label['1']};
	color: ${COLOR_TEXT_MAIN};
	margin: 0;
`;

// Renders a "⌃" chevron glyph, so it still needs the themed family; the glyph size
// and tight line-height are set locally because no type token is sized for an icon.
const NotesCollapse = Styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border: none;
	background: none;
	${props => props.theme.sys.font.body['1']};
	font-size: 16px;
	line-height: 1;
	color: ${COLOR_TEXT_MAIN};
	cursor: pointer;
`;

const NotesBody = Styled.div`
	padding: 16px;
`;

const NotesHeaderRight = Styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

// Low-emphasis meta text ("Updated: …") beside the notes-panel title.
const UpdatedText = Styled.span`
	${props => props.theme.sys.font.body['2']};
	color: ${COLOR_TEXT_SECONDARY};
	white-space: nowrap;
`;

// Rotates the chevron when the panel is collapsed.
const Chevron = Styled.span<{$collapsed: boolean}>`
	display: inline-block;
	transition: transform 0.15s ease;
	transform: rotate(${({$collapsed}) => ($collapsed ? '180deg' : '0deg')});
`;

// Bordered, scrollable container for the inline rich-text editor. Greyed out when read-only so
// the card visually communicates that its contents cannot be edited.
const NotesEditorCard = Styled.div<{$readOnly?: boolean}>`
	border: 1px solid ${COLOR_BORDER};
	border-radius: 8px;
	background-color: ${({$readOnly}) => ($readOnly ? COLOR_SURFACE_DISABLED : COLOR_SURFACE)};
	padding: 8px 12px;
	.editor {
		margin: 0;
	}
`;

type CompanyViewOverviewProps = {
	clientId: string;
	notes: string | null;
	notesUpdatedAt: string | null;
	/** When provided, replaces the Guest Users section in the main column (used by the Contacts module). */
	mainContent?: React.ReactNode;
	/** Whether the current user is permitted to edit this client (notes, in this component). Computed by the caller. */
	canEditClient: boolean;
};

const formatUpdatedAt = (value: string): string => Moment.utc(value).local().format('MMM D, YYYY, [@] h:mma');

const CompanyViewOverview: React.FC<CompanyViewOverviewProps> = ({
	clientId,
	notes,
	notesUpdatedAt,
	mainContent,
	canEditClient,
}) => {
	const {formatMessage} = useIntl();
	const noEditTooltip = formatMessage({id: 'company_view_page.no_edit_permission_tooltip'});
	const [collapsed, setCollapsed] = useState(false);
	// Empty-state "Create Note" reveals the editor inline before any content exists.
	const [creating, setCreating] = useState(false);

	// Blank content (null or an empty rich-text doc) shows the empty state, not an empty editor.
	const showEditor = !isBlankDraftJsContent(notes) || creating;

	// Save on blur — the RTE wrapper fires onDraftJsChanged with the serialized content when the
	// editor loses focus. It skips a blur only while a non-empty note is unchanged since the editor
	// opened, so this still runs for a blank editor and for every blur after a first save, and the
	// checks below still decide whether anything is saved.
	const handleNotesBlur = (newNotes: string): void => {
		// The read-only `RichTextEditorDraftJsWrapper` prop is what actually keeps the user from
		// reaching this handler through the UI. This check is a second, independent layer: it makes
		// it structurally impossible for a mutation to fire from this component even if a future
		// change to the editor's read-only rendering accidentally re-enables input. Skipping the
		// mutation here matters because the backend has no way to reject this write cleanly — an
		// unauthorized request comes back as an opaque internal-error message, not a clear
		// permission error, so the correct behaviour is to never send it in the first place.
		if (!canEditClient) {
			return;
		}
		// Blurred while blank → drop back to the empty state.
		if (isBlankDraftJsContent(newNotes)) {
			setCreating(false);
		}
		// Same content → nothing to save. Not a byte compare: the editor round-trips the note through
		// HTML on every blur, Draft.js re-keys every block on the way back, and for some shapes the
		// export comes back reshaped (alignment dropped, mixed lists re-sequenced, h4-h6 flattened, a
		// code-block deleted), so the serialized strings never match even when nothing was edited.
		// `isSameDraftJsContent` asks the directional question instead — is this export what the editor
		// produces from the stored note with no edit? — so the stored note goes first. Blank-vs-blank
		// (Create Note → blur with no input) is "same content" too.
		if (isSameDraftJsContent(notes, newNotes)) {
			return;
		}
		// Store the note without its edge whitespace. Compare and save go through the same trim, so
		// they cannot disagree about what an edge is, and UpdateClient returns notes beside
		// notesUpdatedAt, so the key={notesUpdatedAt} remount lands on the stored, trimmed note.
		Util.CommitMutation(UpdateClientMutation, {id: clientId, notes: trimDraftJsEdges(newNotes)});
	};

	return (
		<Layout>
			{mainContent != null ? (
				// Contacts module active: slot in the panel without extra padding so ContactsListPanel
				// controls its own spacing (same flex child position as MainColumn).
				<div style={{flex: '1 1 auto', minWidth: 0, overflow: 'hidden'}}>{mainContent}</div>
			) : (
				// Fallback column, shown only when the contacts module is off. Both buttons are
				// inert placeholders today, but they are gated now rather than later: every
				// CLIENT_UPDATE-gated control on this page is meant to be disabled, and without the
				// gate whoever wires these up inherits a page that is read-only everywhere except
				// two live-looking create buttons, with nothing in the code to signal the omission.
				<MainColumn>
					<SectionHeader>
						<SectionTitle>{formatMessage({id: 'company_view_page.guest_users'})}</SectionTitle>
						<ForecastTooltip
							content={canEditClient ? undefined : noEditTooltip}
							spanStyle={{display: 'inline-flex'}}
						>
							<CreateButton type="button" disabled={!canEditClient}>
								{formatMessage({id: 'company_view_page.create_guest_user'})}
							</CreateButton>
						</ForecastTooltip>
					</SectionHeader>
					<EmptyCard>
						<EmptyContent>
							<EmptyMessage>{formatMessage({id: 'company_view_page.guest_users_empty'})}</EmptyMessage>
							<ForecastTooltip
								content={canEditClient ? undefined : noEditTooltip}
								spanStyle={{display: 'inline-flex'}}
							>
								<EmptyActionButton type="button" disabled={!canEditClient}>
									{formatMessage({id: 'company_view_page.create_guest_user'})}
								</EmptyActionButton>
							</ForecastTooltip>
						</EmptyContent>
					</EmptyCard>
				</MainColumn>
			)}

			<NotesPanel data-cy="company-view-notes-panel">
				<NotesHeader>
					<NotesTitle>{formatMessage({id: 'company_view_page.notes_title'})}</NotesTitle>
					<NotesHeaderRight>
						{notesUpdatedAt && (
							<UpdatedText data-cy="company-view-notes-updated">
								{formatMessage(
									{id: 'company_view_page.notes_updated_at'},
									{date: formatUpdatedAt(notesUpdatedAt)}
								)}
							</UpdatedText>
						)}
						<NotesCollapse
							type="button"
							data-cy="company-view-notes-collapse"
							aria-expanded={!collapsed}
							aria-label={formatMessage({
								id: collapsed ? 'company_view_page.notes_expand' : 'company_view_page.notes_collapse',
							})}
							onClick={() => setCollapsed(prev => !prev)}
						>
							<Chevron $collapsed={collapsed} aria-hidden="true">
								{'⌃'}
							</Chevron>
						</NotesCollapse>
					</NotesHeaderRight>
				</NotesHeader>
				{!collapsed && (
					<NotesBody>
						{showEditor ? (
							<ForecastTooltip content={canEditClient ? undefined : noEditTooltip} spanStyle={{display: 'block'}}>
								<NotesEditorCard $readOnly={!canEditClient}>
									<RichTextEditorDraftJsWrapper
										// Re-seed the uncontrolled editor when the saved content changes (e.g. external update).
										key={notesUpdatedAt ?? 'new'}
										id={`company-view-notes-${clientId}`}
										data-cy="company-view-notes-editor"
										initialDraftJsContent={notes ?? ''}
										onDraftJsChanged={handleNotesBlur}
										readOnly={!canEditClient}
										placeholder={formatMessage({id: 'company_view_page.notes_placeholder'})}
										minHeight="240px"
										maxHeight="max(240px, calc(100vh - 360px))"
									/>
								</NotesEditorCard>
							</ForecastTooltip>
						) : (
							<EmptyCard>
								<EmptyContent>
									<EmptyMessage>{formatMessage({id: 'company_view_page.notes_empty'})}</EmptyMessage>
									<ForecastTooltip content={canEditClient ? undefined : noEditTooltip}>
										<EmptyActionButton
											type="button"
											data-cy="company-view-notes-create"
											disabled={!canEditClient}
											onClick={() => setCreating(true)}
										>
											{formatMessage({id: 'company_view_page.create_note'})}
										</EmptyActionButton>
									</ForecastTooltip>
								</EmptyContent>
							</EmptyCard>
						)}
					</NotesBody>
				)}
			</NotesPanel>
		</Layout>
	);
};

export default CompanyViewOverview;
